import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChevronLeft, ChevronRight, Check, CheckCircle2, Copy, FileText, CalendarCheck, Phone, Eye, PenTool, Upload } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import * as LucideIcons from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useRazorpay } from 'react-razorpay';
import html2pdf from 'html2pdf.js';
import { ensureGujaratiFontReady } from '../utils/gujFont';

import { jwtDecode } from "jwt-decode";

// Dynamic Icon Component — accepts PascalCase ("HeartPulse") or kebab/snake
// case ("heart-pulse") and falls back to FileText only if nothing matches.
const DynamicIcon = ({ name }) => {
  const key = LucideIcons[name] ? name : (name || '')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join('');
  const IconComponent = LucideIcons[key] || LucideIcons.FileText;
  return <IconComponent className="w-5 h-5 text-[#172554]" />;
};

export default function SubCategory() {
  const { category_slug, subcategory_slug, step } = useParams();
  const navigate = useNavigate();
  const { Razorpay } = useRazorpay();
  
  const [categoryName, setCategoryName] = useState('');
  const [subCategories, setSubCategories] = useState([]);
  const [discountPct, setDiscountPct] = useState(0);
  const [selectedSub, setSelectedSub] = useState(null);
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [applicationNumber, setApplicationNumber] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [savedAppDoc, setSavedAppDoc] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  
  const [selectedAppType, setSelectedAppType] = useState(null); // 'normal' | 'tatkal' | null (must be picked on Preview when tatkal_enabled)
  const [draftId, setDraftId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const sigPads = useRef({}); // Refs for signature canvases
  
  const activeSubData = Array.isArray(subCategories) ? subCategories.find(s => s.title === selectedSub) || null : null;
  const isTypeRequired = true; // Plan selector always shown on Preview
  const hasChosenType = selectedAppType === 'normal' || selectedAppType === 'tatkal';

  // --- Fixed-format regex validation --------------------------------------
  // A field with a non-empty `pattern` is a "Fixed Format" field (PAN,
  // Aadhaar, GSTIN…). We validate on every keystroke and block Submit
  // until every required + touched fixed field matches its pattern.
  const validateField = (field, value) => {
    if (!field?.pattern) return '';
    // Empty is only an error when the field is required — otherwise let the
    // "required" system handle it separately.
    if (!value) return field.required ? (field.error_message || `Please enter ${field.label}`) : '';
    try {
      const re = new RegExp(field.pattern);
      return re.test(String(value))
        ? ''
        : (field.error_message || `Invalid ${field.label}`);
    } catch (_e) {
      // Broken regex in DB — don't lock the user out, just skip validation.
      return '';
    }
  };

  const activeFields = Array.isArray(activeSubData?.active_fields) ? activeSubData.active_fields : [];
  const fieldErrors = activeFields.reduce((acc, f) => {
    const err = validateField(f, formData[f.name]);
    if (err) acc[f.name] = err;
    return acc;
  }, {});
  const hasFixedFormatErrors = activeFields.some(f => {
    if (!f?.pattern) return false;
    // Required fixed field: must be non-empty AND match.
    if (f.required) return !!validateField(f, formData[f.name]);
    // Optional fixed field: only blocks when the user actually typed something invalid.
    const v = formData[f.name];
    return !!v && !!validateField(f, v);
  });

  const canPay = hasChosenType && !hasFixedFormatErrors;

  const [myGreenCard, setMyGreenCard] = useState(null);
  const [gcSettings, setGcSettings] = useState(null);

  useEffect(() => {
    const fetchGC = async () => {
      const token = localStorage.getItem('userToken');
      if (!token) return;
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const [gcRes, setRes] = await Promise.all([
          axios.get(`${API_URL}/api/greencard/my-card`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/api/greencard/settings`)
        ]);
        if (gcRes.data?.status === 'Active' && new Date(gcRes.data.valid_until) >= new Date()) {
          setMyGreenCard(gcRes.data);
          setGcSettings(setRes.data);
        }
      } catch (e) {
        // ignore
      }
    };
    fetchGC();
  }, []);

  // Per-plan pricing — shown inline on each selectable card.
  const normalOrig = activeSubData?.fee_amount ?? 50;
  const tatkalOrig = activeSubData?.tatkal_fee_amount ?? 150;
  
  const activeDiscountPct = (myGreenCard && gcSettings?.discount_percentage > 0) ? gcSettings.discount_percentage : discountPct;
  const isGCDiscount = myGreenCard && gcSettings?.discount_percentage > 0;
  
  const applyDiscount = (fee) => fee - Math.round(fee * activeDiscountPct / 100);
  const normalFee = applyDiscount(normalOrig);
  const tatkalFee = applyDiscount(tatkalOrig);
  const ORIGINAL_FEE = selectedAppType === 'tatkal' ? tatkalOrig : normalOrig;

  // Dynamic Pricing Logic — only meaningful once the user has picked.
  const FEE_AMOUNT = selectedAppType === 'tatkal' ? tatkalFee : normalFee;
  const DISCOUNT_AMOUNT = ORIGINAL_FEE - FEE_AMOUNT;
  const GST = Math.round(FEE_AMOUNT * 0.18);
  const TOTAL_AMOUNT = FEE_AMOUNT + GST;

  // When the sub-service does NOT offer તત્કાલ, the plan selector still
  // shows both cards (so the user can always see the choice) but we keep
  // the selectedAppType as null on load until the user explicitly picks.
  useEffect(() => {
    // No auto-pick: force explicit choice on Preview to guarantee the user
    // sees Normal vs Urgent and the "must pick" hint fires when unpicked.
  }, [activeSubData]);

  useEffect(() => {
    const fetchSubCategories = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        // First get all services to find the categoryName from category_slug
        const svcRes = await axios.get(`${API_URL}/api/services`);
        const cat = (Array.isArray(svcRes.data) ? svcRes.data : []).find(c => c.slug === category_slug || encodeURIComponent(c.title) === category_slug);
        
        if (!cat) {
            toast.error('Category not found');
            setLoading(false);
            navigate('/');
            return;
        }
        
        const catName = cat.title;
        setCategoryName(catName);
        setDiscountPct(Math.max(0, Math.min(100, Number(cat?.discount_percentage) || 0)));

        const res = await axios.get(`${API_URL}/api/subservices?category_name=${encodeURIComponent(catName)}`);
        const data = Array.isArray(res.data) ? res.data : [];
        setSubCategories(data);
        
        // Auto-select if subcategory_slug exists
        if (subcategory_slug) {
          const subMatch = data.find(s => s.slug === subcategory_slug || encodeURIComponent(s.title) === subcategory_slug);
          if (subMatch) {
            setSelectedSub(subMatch.title);
            // If the user navigates directly to /cat/sub without /form or /checkout, 
            // instantly push them to /form to skip the detail page
            if (!step && !window.location.search.includes('draft=') && !window.location.search.includes('app=')) {
                navigate(`/${category_slug}/${subcategory_slug}/form`, { replace: true });
            }
          } else {
            setSelectedSub(null);
          }
        } else {
          setSelectedSub(null);
          // Fallback to query param for backward compatibility
          const searchParams = new URLSearchParams(window.location.search);
          const subParam = searchParams.get('sub');
          if (subParam && data.some(s => s.title === subParam)) {
            setSelectedSub(subParam);
          }
        }

        const searchParams = new URLSearchParams(window.location.search);

        // Draft resume: /category/<name>?draft=<id> → pre-fill form + type
        const draftParam = searchParams.get('draft');
        const previewParam = searchParams.get('preview');
        const appParam = searchParams.get('app'); // if resumed from actual application

        if (draftParam) {
          const userToken = localStorage.getItem('userToken');
          if (userToken) {
            try {
              const draftRes = await axios.get(`${API_URL}/api/drafts/${draftParam}`, { headers: { Authorization: `Bearer ${userToken}` } });
              const dd = draftRes.data;
              setDraftId(dd.id);
              if (dd.sub_category_name && data.some(s => s.title === dd.sub_category_name)) {
                setSelectedSub(dd.sub_category_name);
              }
              setFormData(dd.form_data || {});
              if (dd.application_type === 'Urgent') setSelectedAppType('tatkal');
              
              if (previewParam === 'true') {
                 navigate(`/${category_slug}/${subcategory_slug}/checkout`);
                 if (dd.application_number) {
                   const appRes = await axios.get(`${API_URL}/api/applications/${dd.application_number}`, { headers: { Authorization: `Bearer ${userToken}` } }).catch(()=>null);
                   if (appRes && appRes.data) {
                      setSavedAppDoc(appRes.data);
                      setApplicationNumber(appRes.data.application_number);
                   }
                 }
              } else {
                 toast.success('ડ્રાફ્ટ લોડ થઈ ગયો');
              }
            } catch (dErr) {
              toast.error('ડ્રાફ્ટ લોડ કરવામાં નિષ્ફળ');
            }
          }
        } else if (appParam && previewParam === 'true') {
          // If we resume directly from a created application (e.g. payment_pending)
          const userToken = localStorage.getItem('userToken');
          if (userToken) {
             try {
               const appRes = await axios.get(`${API_URL}/api/applications/${appParam}`, { headers: { Authorization: `Bearer ${userToken}` } });
               const appDoc = appRes.data;
               if (appDoc.sub_category_name && data.some(s => s.title === appDoc.sub_category_name)) {
                 setSelectedSub(appDoc.sub_category_name);
               }
               setFormData(appDoc.form_data || {});
               if (appDoc.application_type === 'Urgent') setSelectedAppType('tatkal');
               
               navigate(`/${category_slug}/${subcategory_slug}/checkout`);
               setSavedAppDoc(appDoc);
               setApplicationNumber(appDoc.application_number);
             } catch (aErr) {
               toast.error('અરજી મળી નહી — કૃપા કરી ફરીથી પ્રયાસ કરો');
               navigate('/my-applications');
             }
          }
        }
        
      } catch (err) {
        toast.error('માહિતી લોડ કરવામાં નિષ્ફળ.');
      } finally {
        setLoading(false);
      }
    };
    fetchSubCategories();
  }, [category_slug, subcategory_slug]);

  // Smart Autofill: load logged-in user's profile so we can prefill matching fields
  useEffect(() => {
    const userPhone = localStorage.getItem('userPhone');
    if (!userPhone) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${API_URL}/api/profile/${userPhone}`)
      .then(res => setUserProfile(res.data))
      .catch(() => {});
  }, []);

  // Whenever a sub-category is chosen (or profile arrives), prefill matching fields
  useEffect(() => {
    if (!activeSubData || !userProfile) return;
    const fields = Array.isArray(activeSubData.active_fields) ? activeSubData.active_fields : [];
    const NAME_KEYS = ['full_name', 'name', 'applicant_name', 'fullname'];
    const PHONE_KEYS = ['mobile', 'phone', 'user_phone', 'mobile_number', 'contact', 'contact_number'];
    const EMAIL_KEYS = ['email', 'email_address', 'user_email', 'mail'];
    setFormData(prev => {
      const next = { ...prev };
      fields.forEach(f => {
        const key = (f.name || '').toLowerCase();
        if (!next[f.name]) {
          if (NAME_KEYS.includes(key) && userProfile.name) next[f.name] = userProfile.name;
          if (PHONE_KEYS.includes(key) && userProfile.phone) next[f.name] = userProfile.phone;
          if (EMAIL_KEYS.includes(key) && userProfile.email) next[f.name] = userProfile.email;
        }
      });
      return next;
    });
    // eslint-disable-next-line
  }, [selectedSub, userProfile]);

  const handleFieldChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (name, file) => {
    setFiles(prev => ({ ...prev, [name]: file }));
  };

  const handleSaveDraft = async () => {
    const userToken = localStorage.getItem('userToken');
    if (!userToken) {
      toast.error('ડ્રાફ્ટ સેવ કરવા માટે લૉગિન કરો');
      return;
    }
    if (!selectedSub) return;
    setSavingDraft(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const payload = {
        id: draftId || undefined,
        category_name: categoryName,
        sub_category_name: selectedSub,
        application_type: selectedAppType === 'tatkal' ? 'Urgent' : 'Normal',
        form_data: formData || {},
      };
      const res = await axios.post(`${API_URL}/api/drafts`, payload, { headers: { Authorization: `Bearer ${userToken}` } });
      setDraftId(res.data.id);
      toast.success('ડ્રાફ્ટ સેવ થયો — તમે "મારી અરજીઓ" માંથી ફરી ચાલુ કરી શકો છો.');
    } catch (e) {
      toast.error('ડ્રાફ્ટ સેવ કરવામાં નિષ્ફળ');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activeSubData) return;

    // Validate required fields + regex patterns
    try {
      if (Array.isArray(activeSubData?.active_fields)) {
        for (const field of activeSubData.active_fields) {
          if (field.required && !formData[field.name]) {
            toast.error(`કૃપા કરીને ${field.label} દાખલ કરો.`);
            return;
          }
          const patternErr = validateField(field, formData[field.name]);
          if (patternErr) {
            toast.error(patternErr);
            return;
          }
        }
      }
    } catch (error) {
      console.error('CRASHING AT active_fields validation:', activeSubData?.active_fields, error);
    }

    // Add userPhone (token subject) to the submission if available
    const token = localStorage.getItem('userToken');
    let phoneFromToken = '';
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded && decoded.phone) phoneFromToken = decoded.phone;
      } catch (e) {
        console.error("Token decoding failed", e);
      }
    }
    
    // Process skipped uploads based on toggle states
    const skippedUploads = [];
    if (Array.isArray(activeSubData?.required_uploads)) {
        activeSubData.required_uploads.forEach(upload => {
            const isSkipped = formData[`skip_${upload.name}`];
            if (isSkipped) {
                skippedUploads.push(upload.name);
            } else if (upload.required && !files[upload.name]) {
                toast.error(`કૃપા કરીને ${upload.label} જોડો.`);
                throw new Error("Missing required upload");
            }
        });
    }
    
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const form = new FormData();
    // Inject the authenticated phone and skipped uploads into the form data
    const finalFormData = { 
      ...formData, 
      user_phone: phoneFromToken || formData.mobile || '',
      skipped_uploads: JSON.stringify(skippedUploads)
    };
    
    // Add application type based on toggle selection
    const appTypeString = selectedAppType === 'tatkal' ? 'તત્કાલ અરજી (Urgent)' : 'સામાન્ય અરજી (Normal)';
    
    form.append('formData', JSON.stringify(finalFormData));
    form.append('categoryName', categoryName);
    form.append('subCategoryName', selectedSub);
    form.append('applicationType', appTypeString);
    form.append('amount', TOTAL_AMOUNT);
    if (activeSubData.important_note) {
      form.append('importantNote', activeSubData.important_note);
    }
    
    Object.entries(files).forEach(([key, f]) => {
      if (f) form.append(`files[${key}]`, f);
    });
    
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/applications`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSavedAppDoc(res.data.application);
      setApplicationNumber(res.data.application_number);
      toast.success('તમારી અરજીની પુષ્ટિ માટે તૈયાર છે.');
      
      // Google Ads Conversion Tracking (Form Submission)
      if (window.gtag) {
        window.gtag('event', 'generate_lead', {
          'event_category': 'form_submission',
          'event_label': selectedSub
        });
      }

      navigate(`/${category_slug}/${subcategory_slug}/checkout`);
    } catch (err) {
      toast.error('અરજી સાચવવામાં નિષ્ફળ.');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!savedAppDoc || !applicationNumber) return;
    // Defense-in-depth: block payment if user hasn't picked a plan. The
    // button is already disabled by `canPay`, this guards direct
    // programmatic invocation.
    if (selectedAppType !== 'normal' && selectedAppType !== 'tatkal') {
      toast.error('કૃપા કરીને પહેલા અરજીનો પ્રકાર પસંદ કરો (Please select application type first)');
      return;
    }
    
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Amount should be in paise
      const orderRes = await axios.post(`${API_URL}/api/create-order`, { application_number: applicationNumber, application_type: selectedAppType });
      
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderRes.data.amount,
        currency: "INR",
        order_id: orderRes.data.id,
        name: "કાગજબુક ઓનલાઈન",
        description: selectedSub,
        handler: async (res) => {
          setLoading(true);
          try {
            await axios.post(`${API_URL}/api/applications/verify-payment/${applicationNumber}`, {
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_signature: res.razorpay_signature
            });
            toast.success('ચુકવણી સફળ રહી.');
            
            // Google Ads Conversion Tracking (Payment)
            if (window.gtag) {
              window.gtag('event', 'purchase', {
                'event_category': 'payment_complete',
                'event_label': selectedSub,
                'value': orderRes.data.amount / 100,
                'currency': 'INR',
                'transaction_id': res.razorpay_payment_id
              });
            }

            setIsSubmitted(true);
            navigate(`/${category_slug}/${subcategory_slug}`);
          } catch(err) {
             toast.error('ચુકવણીની પુષ્ટિ કરવામાં ભૂલ.');
          } finally {
             setLoading(false);
          }
        },
        prefill: {
          name: formData.full_name || "",
          contact: formData.mobile || formData.user_phone || ""
        },
        theme: {
          color: "#172554"
        }
      };

      const rzp = new Razorpay(options);
      rzp.on('payment.failed', function (response){
         toast.error("ચુકવણી નિષ્ફળ: " + response.error.description);
      });
      rzp.open();
    } catch (err) {
      toast.error('ઓર્ડર બનાવવામાં નિષ્ફળ.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans flex flex-col">
      <Header />
      
      <main className="flex-1 w-full bg-[#F8FAFC]">
        
        {/* Breadcrumb Area */}
        <div className="px-4 md:px-24 py-2 text-sm text-[#172554] font-bold text-gujarati">
          <Link to="/" className="hover:underline">હોમ</Link> &gt; {categoryName}
        </div>

        <div className="px-4 md:px-24 pb-12">
          
          {!selectedSub ? (
            <>
              {/* Title Section matching the image perfectly */}
              <div className="text-center pt-2 pb-8">
                <h2 className="text-xl md:text-[22px] font-bold text-[#172554] text-gujarati">
                  કૃપા કરીને આપ જે {categoryName} મેળવવા માંગો છો તે પસંદ કરો.
                </h2>
                <p className="text-[#172554] text-sm font-medium tracking-wide mt-0.5">
                  Please select atleast one government documents service
                </p>
              </div>

              {loading ? (
                <div className="text-center py-10 text-[#172554]">Loading...</div>
              ) : !subCategories || subCategories.length === 0 ? (
                <div className="text-center py-10 text-[#172554] font-bold text-gujarati">કોઈ પેટા-સેવાઓ ઉપલબ્ધ નથી.</div>
              ) : (
                <div className="max-w-[800px] mx-auto flex flex-col gap-[3px]">
                  {(() => {
                    if (!Array.isArray(subCategories)) return null;
                    try {
                      return subCategories.map((sub, idx) => (
                        <div 
                          key={idx}
                          onClick={() => navigate(`/${category_slug}/${sub.slug || encodeURIComponent(sub.title)}/form`)}
                          className="flex bg-white border border-[#E2E8F0] h-[38px] items-center cursor-pointer hover:bg-gray-50 transition-colors"
                        >
                          <div className="w-12 h-full border-r border-[#E2E8F0] flex items-center justify-center shrink-0 bg-white">
                            <div className="w-6 h-5 border border-black rounded-[2px] flex flex-col items-center justify-center p-[2px]">
                               <DynamicIcon name={sub.icon_type} />
                            </div>
                          </div>
                          <div className="px-4 font-bold text-gujarati text-[#172554] text-[15px] flex-1 truncate">
                            {sub.title}
                          </div>
                          {activeDiscountPct > 0 && (
                            <div data-testid={`sub-price-${idx}`} className="flex items-center gap-2 pr-3 shrink-0 text-sm">
                              <span className="line-through text-gray-400">₹{sub.fee_amount ?? 50}</span>
                              <span className="font-extrabold text-[#172554]">₹{(sub.fee_amount ?? 50) - Math.round((sub.fee_amount ?? 50) * activeDiscountPct / 100)}</span>
                              <span className="bg-green-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full text-gujarati">{activeDiscountPct}% છૂટ</span>
                            </div>
                          )}
                        </div>
                      ));
                    } catch (error) {
                      console.error('CRASHING AT subCategories map:', subCategories, error);
                      return null;
                    }
                  })()}
                </div>
              )}
            </>
          ) : isSubmitted ? (
            <div className="flex flex-col items-center justify-center py-12 gap-6 text-center max-w-2xl mx-auto bg-white rounded-lg shadow mt-8 p-8 border-2 border-[#E2E8F0]">
              <CheckCircle2 className="w-20 h-20 text-green-500" />
              <h3 className="text-2xl font-bold text-[#172554] text-gujarati">
                તમારી '{selectedSub}' માટેની અરજી સબમિટ થઈ ગઈ છે.
              </h3>
              <p className="text-[#64748B] text-gujarati max-w-md">
                તમારો અરજી નંબર <b>#{applicationNumber}</b> છે. 
                અમે ટૂંક સમયમાં તમારો સંપર્ક કરીશું.
              </p>
              <div className="flex flex-wrap gap-3 w-full mt-4 justify-center">
                <button 
                  onClick={async () => {
                     try {
                        const API_URL = process.env.REACT_APP_BACKEND_URL;
                        const res = await axios.get(`${API_URL}/api/applications/receipt/${applicationNumber}`);
                        const element = document.createElement('div');
                        element.innerHTML = res.data;

                        const opt = {
                          margin: 1,
                          filename: `Kagajbook_Receipt_${applicationNumber}.pdf`,
                          image: { type: 'jpeg', quality: 0.98 },
                          html2canvas: { scale: 2 },
                          jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
                        };
                        html2pdf().set(opt).from(element).save();
                     } catch(err) {
                        toast.error('રસીદ ડાઉનલોડ કરવામાં નિષ્ફળ');
                     }
                  }}
                  className="px-6 py-2 bg-white border border-[#172554] text-[#172554] font-bold rounded text-gujarati shadow-sm"
                  data-testid="post-payment-download-receipt-btn"
                >
                  ડાઉનલોડ રસીદ (Receipt)
                </button>
                <button
                  onClick={async () => {
                    try {
                      const API_URL = process.env.REACT_APP_BACKEND_URL;
                      toast.info('અરજી ફોર્મ તૈયાર થઈ રહ્યું છે... (Preparing your application PDF)');
                      const res = await axios.get(
                        `${API_URL}/api/applications/${applicationNumber}/pack`,
                        { responseType: 'blob' }
                      );
                      const blob = new Blob([res.data], { type: 'application/pdf' });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `${applicationNumber}_Complete_Application.pdf`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      setTimeout(() => URL.revokeObjectURL(url), 4000);
                    } catch (e) {
                      console.error(e);
                      toast.error('અરજી ફોર્મ ડાઉનલોડ કરવામાં નિષ્ફળ');
                    }
                  }}
                  className="px-6 py-2 bg-[#F5B800] hover:bg-[#FFC928] text-[#172554] font-extrabold rounded text-gujarati border-2 border-[#F5B800] shadow-sm"
                  data-testid="post-payment-download-pack-btn"
                >
                  ડાઉનલોડ અરજી ફોર્મ
                </button>
                <button 
                  onClick={() => { setSelectedSub(null); setIsSubmitted(false); navigate(`/track/${applicationNumber}`); }}
                  className="px-6 py-2 bg-[#172554] text-white font-bold rounded hover:opacity-90 text-gujarati"
                >
                  અરજીની સ્થિતિ તપાસો
                </button>
              </div>
            </div>
          ) : !step ? (
            <div data-testid="subcategory-redirect-loader" className="flex justify-center py-20">
              <div className="w-8 h-8 border-4 border-[#F5B800] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : step === 'checkout' ? (
            <div className="max-w-2xl mx-auto mt-6 bg-white p-8 rounded-lg shadow-sm border border-[#E2E8F0]">
              <h3 className="text-xl font-bold text-[#172554] text-gujarati border-b pb-4 mb-4">
                અરજી પ્રીવ્યુ (Preview)
              </h3>
              
              <div className="flex flex-col gap-3 mb-6 bg-gray-50 p-4 rounded text-[#172554] text-sm">
                <p><b>મુખ્ય શ્રેણી:</b> {categoryName}</p>
                <p><b>પેટા-સેવા:</b> {selectedSub}</p>
                <p><b>અંદાજીત સમય (Est. Days):</b> <span className="bg-blue-100 text-blue-800 w-fit px-2 py-0.5 rounded font-bold">{activeSubData?.estimated_days || '3-5 દિવસ'}</span></p>
                
                {(() => {
                  const entries = Object.entries(formData || {});
                  if (!Array.isArray(entries)) return null;
                  try {
                    return entries.map(([k, v]) => {
                      if (k.startsWith('skip_')) return null;
                      const fields = Array.isArray(activeSubData?.active_fields) ? activeSubData.active_fields : [];
                      const f = fields.find(f => f.name === k);
                      const label = f?.label || k;
                      if (f?.type === 'signature') {
                        return (
                          <div key={k} className="col-span-full border-t border-[#E2E8F0] mt-2 pt-2">
                            <p><b>{label}:</b></p>
                            {v && <img src={v} alt="Signature" className="h-16 mt-1 border rounded bg-white" />}
                          </div>
                        );
                      }
                      return <p key={k}><b>{label}:</b> {v}</p>;
                    });
                  } catch (error) {
                    console.error('CRASHING AT formData entries map:', entries, error);
                    return null;
                  }
                })()}
                {Object.keys(files).length > 0 && (
                   <p><b>અપલોડ કરેલ દસ્તાવેજો:</b> {Object.keys(files).length}</p>
                )}
                {(() => {
                  try {
                    const skippedCount = Object.keys(formData).filter(k => k.startsWith('skip_') && formData[k]).length;
                    if (skippedCount > 0) return <p className="text-orange-600"><b>અવગણેલા (Skipped) દસ્તાવેજો:</b> {skippedCount}</p>;
                  } catch (e) {}
                  return null;
                })()}
              </div>
              
              <div className="bg-[#F8FAFC] p-4 rounded border border-[#E2E8F0] mb-6 flex flex-col gap-3">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="font-bold text-[#172554] text-gujarati">ચુકવણીની વિગતો (Payment Details)</h4>
                  {discountPct > 0 && <span data-testid="preview-discount-badge" className="bg-green-600 text-white text-xs font-bold px-2 py-0.5 rounded-full text-gujarati">{discountPct}% છૂટ</span>}
                </div>

                {/* --- Important Note --- */}
                {activeSubData?.important_note && (
                  <div className="bg-yellow-50 border-l-4 border-[#F5B800] p-4 rounded-r-md mb-2 shadow-sm" data-testid="preview-important-note">
                    <h5 className="text-[#172554] text-xs font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#F5B800] inline-block"></span>
                      મહત્વની નોંધ (Important Note)
                    </h5>
                    <p className="text-[#334155] text-sm text-gujarati leading-relaxed whitespace-pre-wrap">
                      {activeSubData.important_note}
                    </p>
                  </div>
                )}

                {/* --- Plan Selector (Navy vs Yellow cards) ---
                    Renders unconditionally on Preview so the user always
                    picks Normal vs Urgent BEFORE paying. */}
                {(() => { console.log('Rendering Plan Selector'); return null; })()}
                <div data-testid="plan-selector" className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
                  <button
                    type="button"
                    data-testid="plan-normal"
                    onClick={() => setSelectedAppType('normal')}
                    className={`text-left rounded-xl p-4 border-2 transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-[#172554]/40 ${
                      selectedAppType === 'normal'
                        ? 'bg-[#172554] border-[#172554] text-white shadow-md scale-[1.01]'
                        : 'bg-white border-[#172554]/30 text-[#172554] hover:border-[#172554]'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex flex-col flex-1">
                        <span className={`text-[10px] font-extrabold uppercase tracking-wider ${selectedAppType === 'normal' ? 'text-[#F5B800]' : 'text-[#F5B800]'}`}>Normal Plan</span>
                        <span className="font-extrabold text-gujarati text-base leading-tight mt-1">સામાન્ય અરજી</span>
                        <span className={`text-[11px] mt-1 text-gujarati ${selectedAppType === 'normal' ? 'text-white/80' : 'text-gray-500'}`}>
                          {activeSubData?.estimated_days || '3-5 દિવસ'}
                        </span>
                      </div>
                      <div className={`text-right ${selectedAppType === 'normal' ? 'text-[#F5B800]' : 'text-[#172554]'}`}>
                        {activeDiscountPct > 0 && <div data-testid="plan-normal-original" className="text-xs line-through opacity-60">₹{normalOrig}</div>}
                        <div data-testid="plan-normal-fee" className="text-xl font-extrabold leading-none">₹{normalFee}</div>
                        <div className={`text-[9px] uppercase tracking-wider mt-1 ${selectedAppType === 'normal' ? 'text-white/70' : 'text-gray-500'}`}>+ GST</div>
                      </div>
                    </div>
                    {selectedAppType === 'normal' && (
                      <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold text-[#172554] bg-[#F5B800] px-2 py-0.5 rounded-full">
                        ✓ પસંદ કરેલ
                      </div>
                    )}
                  </button>

                  <button
                    type="button"
                    data-testid="plan-tatkal"
                    onClick={() => setSelectedAppType('tatkal')}
                    className={`text-left rounded-xl p-4 border-2 transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-[#F5B800]/50 ${
                      selectedAppType === 'tatkal'
                        ? 'bg-[#F5B800] border-[#F5B800] text-[#172554] shadow-md scale-[1.01]'
                        : 'bg-white border-[#F5B800] text-[#172554] hover:bg-[#FFF8DB]/40'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex flex-col flex-1">
                        <span className="text-[10px] font-extrabold uppercase tracking-wider text-[#172554]">⚡ Urgent Plan</span>
                        <span className="font-extrabold text-gujarati text-base leading-tight mt-1">તત્કાલ અરજી</span>
                        <span className="text-[11px] mt-1 text-gujarati text-[#172554]/70">ઝડપી પ્રક્રિયા</span>
                      </div>
                      <div className="text-right text-[#172554]">
                        {activeDiscountPct > 0 && <div data-testid="plan-tatkal-original" className="text-xs line-through opacity-60">₹{tatkalOrig}</div>}
                        <div data-testid="plan-tatkal-fee" className="text-xl font-extrabold leading-none">₹{tatkalFee}</div>
                        <div className="text-[9px] uppercase tracking-wider mt-1 text-[#172554]/70">+ GST</div>
                      </div>
                    </div>
                    {selectedAppType === 'tatkal' && (
                      <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold text-[#F5B800] bg-[#172554] px-2 py-0.5 rounded-full">
                        ✓ પસંદ કરેલ
                      </div>
                    )}
                  </button>
                </div>
                {!hasChosenType && (
                  <p
                    data-testid="plan-selector-hint"
                    className="text-xs text-red-600 text-gujarati font-semibold -mt-1"
                  >
                    ચુકવણી પહેલા અરજીનો પ્રકાર પસંદ કરવો જરૂરી છે.
                  </p>
                )}

                <div className="border-t border-[#E2E8F0] pt-3 mt-1 flex flex-col gap-2">
                  {isGCDiscount && hasChosenType && (
                    <div className="flex justify-between items-center bg-green-50 text-green-700 font-bold p-2 rounded mb-1 border border-green-200" data-testid="preview-gc-discount-badge">
                      <span className="text-gujarati text-xs">ગ્રીન કાર્ડ ડિસ્કાઉન્ટ {activeDiscountPct}% લાગુ</span>
                      <LucideIcons.HeartPulse className="w-4 h-4 text-green-600" />
                    </div>
                  )}
                  {activeDiscountPct > 0 && hasChosenType && (
                    <>
                      <div className="flex justify-between">
                        <span>મૂળ ફી (Original Fee):</span>
                        <span data-testid="preview-original-fee" className="line-through text-gray-500">₹{ORIGINAL_FEE}</span>
                      </div>
                      <div className="flex justify-between text-green-700 font-semibold">
                        <span>છૂટ (Discount {activeDiscountPct}%):</span>
                        <span data-testid="preview-discount-amount">- ₹{DISCOUNT_AMOUNT}</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between">
                    <span>ફી (Fees):</span>
                    <span data-testid="preview-fee-amount">
                      {!hasChosenType
                        ? <span className="text-gray-400">—</span>
                        : `₹${FEE_AMOUNT}`}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>જી.એસ.ટી (GST) (૧૮%):</span>
                    <span data-testid="preview-gst-amount">
                      {!hasChosenType
                        ? <span className="text-gray-400">—</span>
                        : `₹${GST}`}
                    </span>
                  </div>
                  <hr className="border-[#E2E8F0]"/>
                  <div className="flex justify-between font-bold text-[#172554] text-lg">
                    <span>કુલ (Total):</span>
                    <span data-testid="preview-total-amount">
                      {!hasChosenType
                        ? <span className="text-gray-400">—</span>
                        : `₹${TOTAL_AMOUNT}`}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                 <button 
                  onClick={() => navigate(`/${category_slug}/${subcategory_slug}/form`)}
                  className="flex-1 bg-white border border-[#172554] text-[#172554] font-bold py-3 rounded text-lg hover:bg-gray-50 transition-colors text-gujarati shadow-sm"
                >
                  પાછળ જાવ (Edit)
                </button>
                <button 
                  onClick={handlePayment}
                  disabled={loading || !canPay}
                  data-testid="preview-pay-btn"
                  title={!canPay ? 'કૃપા કરીને પહેલા અરજીનો પ્રકાર પસંદ કરો' : ''}
                  className="flex-1 bg-[#172554] text-white font-bold py-3 rounded text-lg hover:bg-[#1e3a8a] transition-colors text-gujarati shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Processing...' : 'ચુકવણી અને સબમિટ કરો'}
                </button>
              </div>
            </div>
          ) : isSubmitted ? (
            <div className="flex flex-col items-center justify-center py-12 gap-6 text-center max-w-2xl mx-auto bg-white rounded-lg shadow mt-8 p-8 border-2 border-[#E2E8F0]">
              <CheckCircle2 className="w-20 h-20 text-green-500" />
              <h3 className="text-2xl font-bold text-[#172554] text-gujarati">
                તમારી '{selectedSub}' માટેની અરજી સબમિટ થઈ ગઈ છે.
              </h3>
              <p className="text-[#64748B] text-gujarati max-w-md">
                તમારો અરજી નંબર <b>#{applicationNumber}</b> છે. 
                અમે ટૂંક સમયમાં તમારો સંપર્ક કરીશું.
              </p>
              <div className="flex flex-wrap gap-3 w-full mt-4 justify-center">
                <button 
                  onClick={async () => {
                     try {
                        const API_URL = process.env.REACT_APP_BACKEND_URL;
                        const res = await axios.get(`${API_URL}/api/applications/receipt/${applicationNumber}`);
                        const element = document.createElement('div');
                        element.innerHTML = res.data;

                        const printBtn = element.querySelector('.print-btn');
                        if (printBtn) printBtn.style.display = 'none';

                        // Ensure Gujarati TTF is loaded before html2canvas
                        // snapshots the DOM (otherwise the browser renders
                        // Gujarati as dots/tofu in the exported PDF).
                        await ensureGujaratiFontReady();

                        const opt = {
                          margin:       0.5,
                          filename:     `Receipt_${applicationNumber}.pdf`,
                          image:        { type: 'jpeg', quality: 0.98 },
                          html2canvas:  { scale: 2, useCORS: true, allowTaint: true },
                          jsPDF:        { unit: 'in', format: 'letter', orientation: 'portrait' }
                        };

                        // eslint-disable-next-line
                        html2pdf().from(element).set(opt).save();
                      } catch(e) {
                        console.error("PDF download failed", e);
                      }
                  }}
                  className="px-6 py-2 bg-white border-2 border-[#172554] text-[#172554] font-bold rounded hover:bg-gray-50 text-gujarati"
                  data-testid="post-payment-download-receipt-btn"
                >
                  ડાઉનલોડ રસીદ
                </button>
                <button
                  onClick={async () => {
                    try {
                      const API_URL = process.env.REACT_APP_BACKEND_URL;
                      toast.info('અરજી ફોર્મ તૈયાર થઈ રહ્યું છે... (Preparing your application PDF)');
                      const res = await axios.get(
                        `${API_URL}/api/applications/${applicationNumber}/pack`,
                        { responseType: 'blob' }
                      );
                      const blob = new Blob([res.data], { type: 'application/pdf' });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `${applicationNumber}_Complete_Application.pdf`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      setTimeout(() => URL.revokeObjectURL(url), 4000);
                    } catch (e) {
                      console.error(e);
                      toast.error('અરજી ફોર્મ ડાઉનલોડ કરવામાં નિષ્ફળ');
                    }
                  }}
                  className="px-6 py-2 bg-[#F5B800] hover:bg-[#FFC928] text-[#172554] font-extrabold rounded text-gujarati border-2 border-[#F5B800] shadow-sm"
                  data-testid="post-payment-download-pack-btn"
                >
                  ડાઉનલોડ અરજી ફોર્મ
                </button>
                <button 
                  onClick={() => { setSelectedSub(null); setIsSubmitted(false); navigate(`/track/${applicationNumber}`); }}
                  className="px-6 py-2 bg-[#172554] text-white font-bold rounded hover:opacity-90 text-gujarati"
                >
                  અરજીની સ્થિતિ તપાસો
                </button>
              </div>
            </div>
          ) : step === 'form' ? (
            <div className="max-w-2xl mx-auto mt-6 bg-white p-8 rounded-lg shadow-sm border border-[#E2E8F0]">
              <div className="flex items-center justify-between mb-6 border-b pb-4">
                <h3 className="text-xl font-bold text-[#172554] text-gujarati">
                  અરજી ફોર્મ: <span className="text-blue-700">{selectedSub}</span>
                </h3>
                <button onClick={() => navigate(`/${category_slug}`)} className="text-sm font-bold text-[#64748B] hover:text-black">
                  પાછળ જાવ
                </button>
              </div>

              <div className="flex flex-col gap-3 mb-6 bg-gray-50 p-4 rounded text-[#172554] text-sm border border-gray-200">
                <p><b>મુખ્ય શ્રેણી:</b> {categoryName}</p>
                <p><b>અંદાજીત સમય (Est. Days):</b> <span className="bg-blue-100 text-blue-800 w-fit px-2 py-0.5 rounded font-bold">{activeSubData?.estimated_days || '3-5 દિવસ'}</span></p>
                <p><b>ફી (Fee):</b> <span className="font-bold text-green-700">₹{(activeSubData?.fee_amount ?? 50) - Math.round((activeSubData?.fee_amount ?? 50) * activeDiscountPct / 100)}</span></p>
                
                {activeSubData?.important_note && (
                  <div className="mt-2 bg-yellow-50 border-l-4 border-[#F5B800] p-3 rounded-r-md">
                    <h5 className="text-[#172554] text-xs font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#F5B800] inline-block"></span>
                      મહત્વની નોંધ (Important Note)
                    </h5>
                    <p className="text-[#334155] text-xs text-gujarati leading-relaxed whitespace-pre-wrap">
                      {activeSubData.important_note}
                    </p>
                  </div>
                )}
              </div>

              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await handleSubmit(e);
                } catch (err) {
                  if (err.message === "Missing required upload") return;
                  console.error(err);
                }
              }} className="flex flex-col gap-1.5 bg-[#EFF6FF] p-3 md:p-4 rounded-lg border border-blue-200">
                
                {/* Dynamic Form Fields — Compact Horizontal Rows (Label | Input) */}
                {(() => {
                  const fields = Array.isArray(activeSubData?.active_fields) ? activeSubData.active_fields : [];
                  if (!Array.isArray(fields)) return null;
                  try {
                    return fields.map((field, idx) => {
                      const value = formData[field.name] || '';
                      const isFixed = !!field.pattern;
                      const err = fieldErrors[field.name];
                      const showErr = isFixed && !!value && !!err; // only after user types
                      const showOk = isFixed && !!value && !err;   // live green ✓
                      return (
                        <div key={idx} className="bg-white rounded border border-blue-100 flex flex-col sm:flex-row sm:items-center px-2.5 py-1.5 gap-1.5 sm:gap-3">
                          <label className="sm:w-44 md:w-52 shrink-0 text-[#172554] font-bold text-gujarati text-[13px] flex items-center gap-1.5 leading-tight">
                            <span className="truncate">{field.label} {field.required ? <span className="text-red-500">*</span> : ''}</span>
                            {isFixed && !showOk && (
                              <span
                                className="inline-flex items-center gap-1 text-[9px] font-bold bg-yellow-100 text-yellow-800 px-1 py-0.5 rounded uppercase tracking-wide shrink-0"
                                title={field.error_message || 'Fixed format'}
                                data-testid={`field-fixed-badge-${field.name}`}
                              >
                                Fixed
                              </span>
                            )}
                            {showOk && (
                              <span
                                className="inline-flex items-center gap-1 text-[9px] font-bold bg-green-100 text-green-800 px-1 py-0.5 rounded uppercase tracking-wide shrink-0"
                                data-testid={`field-valid-badge-${field.name}`}
                                title="Format matches"
                              >
                                <CheckCircle2 className="w-2.5 h-2.5" /> Valid
                              </span>
                            )}
                          </label>
                          <div className="relative flex-1 w-full">
                            <input
                              type={field.type || 'text'}
                              required={field.required}
                              pattern={field.pattern || undefined}
                              title={field.error_message || undefined}
                              value={value}
                              onChange={(e) => handleFieldChange(field.name, e.target.value)}
                              data-testid={`field-input-${field.name}`}
                              aria-invalid={showErr ? 'true' : 'false'}
                              className={`w-full border rounded h-8 px-2 text-[13px] focus:outline-none transition-colors ${
                                showErr
                                  ? 'border-red-500 focus:border-red-600 bg-red-50 pr-8'
                                  : showOk
                                    ? 'border-green-500 focus:border-green-600 bg-green-50 pr-8'
                                    : 'border-[#CBD5E1] focus:border-blue-500'
                              }`}
                            />
                            {showOk && (
                              <CheckCircle2
                                className="w-4 h-4 text-green-600 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                                data-testid={`field-valid-icon-${field.name}`}
                              />
                            )}
                            {showErr && (
                              <p
                                className="text-[10px] text-red-600 mt-0.5 font-medium leading-tight"
                                data-testid={`field-error-${field.name}`}
                              >
                                {err}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    });
                  } catch (error) {
                    console.error('CRASHING AT active_fields map:', fields, error);
                    return null;
                  }
                })()}
                
                {/* Dynamic File Upload Boxes */}
                {(() => {
                  const uploads = activeSubData?.required_uploads;
                  if (!Array.isArray(uploads)) return null;
                  if (uploads.length === 0) return null;
                  try {
                    return (
                      <div className="mt-3 border-t pt-3 border-blue-200">
                        <h4 className="text-sm font-bold text-[#172554] text-gujarati mb-2">જરૂરી દસ્તાવેજો જોડો (Upload Documents)</h4>
                        <div className="flex flex-col gap-1.5">
                          {uploads.map((upload, idx) => {
                            const isSkipped = formData[`skip_${upload.name}`] || false;
                            const hasFile = !!files[upload.name];
                            
                            return (
                            <div key={idx} className="bg-white px-2 py-1.5 rounded border border-blue-100 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2.5 min-h-[36px]">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                {/* Col 1: Toggle */}
                                <div className="shrink-0">
                                  <label className="flex items-center cursor-pointer" title={isSkipped ? 'અવગણો (Skip)' : 'જોડો (Upload)'}>
                                    <div 
                                      className={`relative inline-block w-8 h-4 rounded-full transition-colors ${!isSkipped ? 'bg-blue-600' : 'bg-gray-300'}`}
                                      onClick={() => handleFieldChange(`skip_${upload.name}`, !isSkipped)}
                                    >
                                      <span className={`absolute top-0.5 left-0.5 bg-white w-3 h-3 rounded-full transition-transform ${!isSkipped ? 'translate-x-4' : 'translate-x-0'}`}></span>
                                    </div>
                                  </label>
                                </div>
                                
                                {/* Col 2: Label */}
                                <div className="flex-1 min-w-0">
                                  <label className={`block text-[12px] sm:text-[13px] font-bold ${isSkipped ? 'text-gray-400 line-through' : 'text-[#172554]'} break-words whitespace-normal leading-tight`}>
                                    {upload.label} {upload.required && !isSkipped ? <span className="text-red-500">*</span> : ''}
                                  </label>
                                </div>
                              </div>
                              
                              {/* Col 3 & 4: Upload button and Checkmark */}
                              <div className="flex items-center justify-between sm:justify-end gap-2 w-full sm:w-auto shrink-0 pl-10 sm:pl-0">
                                {!isSkipped ? (
                                  <>
                                    <div className="relative flex-1 sm:flex-initial">
                                      <input 
                                        type="file" 
                                        accept={upload.allowed_types && upload.allowed_types !== '*' ? upload.allowed_types : undefined}
                                        required={upload.required && !hasFile}
                                        onChange={(e) => {
                                          const file = e.target.files[0];
                                          if (!file) return;
                                          
                                          const maxSizeMB = upload.max_size_mb || 5;
                                          const sizeMB = file.size / (1024 * 1024);
                                          if (sizeMB > maxSizeMB) {
                                            toast.error(`ફાઈલ ખૂબ મોટી છે (${sizeMB.toFixed(1)} MB). મહત્તમ ${maxSizeMB} MB.`);
                                            e.target.value = '';
                                            return;
                                          }
                                          
                                          if (upload.allowed_types && upload.allowed_types !== '*') {
                                            const allowedArr = upload.allowed_types.split(',');
                                            const isAllowed = allowedArr.some(type => {
                                              if (type === 'image/jpeg' || type === 'image/jpg') {
                                                return file.type === 'image/jpeg' || file.type === 'image/jpg' || file.name.toLowerCase().endsWith('.jpg') || file.name.toLowerCase().endsWith('.jpeg');
                                              }
                                              if (type === 'image/png') {
                                                return file.type === 'image/png' || file.name.toLowerCase().endsWith('.png');
                                              }
                                              if (type === 'application/pdf') {
                                                return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
                                              }
                                              return file.type === type;
                                            });
                                            
                                            if (!isAllowed) {
                                              toast.error(`માન્ય ફોર્મેટ નથી. ફક્ત ${upload.allowed_types.includes('pdf') ? 'PDF' : 'JPG/PNG'} ફોર્મેટ જ માન્ય છે.`);
                                              e.target.value = '';
                                              return;
                                            }
                                          }
                                          
                                          handleFileChange(upload.name, file);
                                        }}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                      />
                                      <button type="button" className="w-full sm:w-auto bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 text-[11px] px-2.5 h-7 rounded font-medium truncate max-w-[200px] sm:max-w-[140px] pointer-events-none flex items-center justify-center sm:justify-start gap-1">
                                        <Upload className="w-3 h-3 shrink-0" />
                                        <span className="truncate">{hasFile ? files[upload.name].name : 'Choose File'}</span>
                                      </button>
                                    </div>
                                    <div className="w-4 flex justify-center shrink-0">
                                      {hasFile && (
                                        <CheckCircle2 className="w-4 h-4 text-green-500" data-testid={`file-uploaded-check-${upload.name}`} />
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <div className="text-[11px] text-orange-500 font-bold italic w-full sm:w-[140px] text-left sm:text-right flex items-center h-7">
                                    બાકાત (Skipped)
                                  </div>
                                )}
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  } catch (error) {
                    console.error('CRASHING AT required_uploads map:', uploads, error);
                    return null;
                  }
                })()}

                {/* Application Type toggle is intentionally not on the initial
                    form — user picks it on the Preview page (above Payment
                    Details) so prices only appear once, at checkout. */}

                <div className="flex flex-col sm:flex-row justify-end items-center gap-3 mt-6">
                  {localStorage.getItem('userToken') && (
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      disabled={savingDraft}
                      data-testid="save-draft-btn"
                      className="w-full sm:w-auto border border-[#172554] text-[#172554] font-bold py-2 px-6 rounded text-sm hover:bg-blue-50 transition-colors text-gujarati shadow-sm flex items-center justify-center gap-2 disabled:opacity-50"
                      title="ડ્રાફ્ટ સેવ કરો - પછી 'મારી અરજીઓ' માંથી ચાલુ રાખો"
                    >
                      {savingDraft ? 'સેવ થઈ રહ્યું છે...' : (draftId ? 'ડ્રાફ્ટ અપડેટ કરો' : 'ડ્રાફ્ટ સેવ કરો')}
                    </button>
                  )}
                  <button 
                    type="submit"
                    disabled={loading || hasFixedFormatErrors}
                    data-testid="confirm-application-btn"
                    title={hasFixedFormatErrors ? 'કૃપા કરીને બધા Fixed Format ફીલ્ડ યોગ્ય રીતે ભરો' : ''}
                    className="w-full sm:w-auto bg-[#F5B800] text-[#172554] font-bold py-2 px-8 rounded text-[15px] hover:bg-[#FFC928] transition-colors text-gujarati shadow-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'અરજી સબમિટ થઈ રહી છે...' : 'પુષ્ટિ કરો (Confirm)'}
                  </button>
                </div>
                {hasFixedFormatErrors && (
                  <div
                    className="text-xs text-red-600 font-medium bg-red-50 border border-red-200 rounded p-2 text-right"
                    data-testid="form-validation-summary"
                  >
                    કૃપા કરીને હાઇલાઇટ કરેલા ફીલ્ડ સાચી રીતે ભરો — Submit ત્યાં સુધી બ્લોક રહેશે.
                  </div>
                )}
              </form>
            </div>
          ) : null}

        </div>
      </main>
      
      <Footer />
    </div>
  );
}